<template>
  <model-form
    ref="modelForm"
    :model="membership"
  >
    <div>
      <div class="row q-col-gutter-md">
        <div class="col-12">
          <q-card
            flat
            bordered
          >
            <q-markup-table
              separator="horizontal"
              bordered
              flat
            >
              <thead>
                <tr>
                  <th>
                    Active
                  </th>
                  <th>
                    Members
                  </th>
                  <th>
                    Price
                  </th>
                  <th>
                    Start Date
                  </th>
                  <th>
                    End Date
                  </th>
                  <th>
                    Term
                  </th>
                  <th>
                    Renewing
                  </th>
                  <th>
                    Renews On
                  </th>
                </tr>
              </thead>

              <tbody>
                <tr>
                  <td class="text-center">
                    <q-icon
                      v-if="membership.effective"
                      name="check"
                      size="lg"
                      color="positive"
                    />
                    <q-icon
                      v-else-if="membership.state === 'pending'"
                      name="warning"
                      size="lg"
                      color="warning"
                    />
                    <q-icon
                      v-else-if="membership.state === 'inactive'"
                      name="cancel"
                      size="lg"
                      color="negative"
                    />
                  </td>
                  <td class="text-center">
                    {{ membership.members.data.length }}
                  </td>
                  <td class="text-center">
                    <span v-if="!$_.isNumber(membership.monthlyRate)">-</span>
                    <i18n-n
                      v-else
                      :class="{ 'text-grey-7': !membership.monthlyRate }"
                      tag="span"
                      :value="membership.monthlyRate"
                      format="currency"
                      scope="global"
                    />
                  </td>
                  <td class="text-center">
                    {{ membership.currentMembershipInterval ? membership.currentMembershipInterval.startDate : 'n/a' }}
                  </td>
                  <td class="text-center">
                    {{ membership.currentMembershipInterval ? membership.currentMembershipInterval.endDate : 'n/a' }}
                  </td>
                  <td class="text-center">
                    {{ membership.term }} month{{ membership.term > 1 ? 's' : '' }}
                  </td>
                  <td class="text-center">
                    <q-icon
                      v-if="!membership.canceledAt"
                      name="check"
                      size="sm"
                      color="positive"
                    />
                    <q-icon
                      v-else
                      name="cancel"
                      size="sm"
                      color="negative"
                    >
                      <q-tooltip>Canceled at {{ membership.canceledAt }} {{ membership.cancleReason }}</q-tooltip>
                    </q-icon>
                    <q-btn
                      v-if="!membership.canceledAt"
                      dense
                      label="Stop Renewing"
                      outline
                      :loading="savingCanceledAt"
                      size="sm"
                      @click="setRenewal(false)"
                    />
                    <q-btn
                      v-else
                      dense
                      label="Turn On Renewal"
                      outline
                      size="sm"
                      :loading="savingCanceledAt"
                      @click="setRenewal(true)"
                    />
                  </td>
                  <td class="text-center">
                    {{ membership.currentMembershipInterval && !membership.canceledAt ? membership.currentMembershipInterval.renewalDate : 'n/a' }}
                    <q-btn
                      v-if="!membership.canceledAt"
                      dense
                      :label="$t('actions.change')"
                      outline
                      size="sm"
                      :disable="!$register"
                      @click="showRenewalDateDialog = true"
                    />
                    <q-tooltip v-if="!$register">
                      Modifying membership only available in register app
                    </q-tooltip>
                  </td>
                </tr>
              </tbody>
            </q-markup-table>
            <q-card-actions class="row justify-center q-ma-sm">
              <q-btn
                v-if="membership.state == 'pending'"
                outline
                :disable="!$register"
                label="Fix Pending Membership Charge"
                color="negative"
                @click="fixPendingMembership"
              />
              <q-btn
                v-else
                outline
                :disable="!$register && !$admin"
                :label="membership.effective ? 'Modify Membership' : 'Start Membership'"
                :color="membership.effective ? null : 'positive'"
                @click="modifyMembership"
              />
            </q-card-actions>
          </q-card>
        </div>
      </div>
    </div>
    <div class="q-py-lg">
      <model-table
        title="Members"
        :collection="membership.members"
        :columns="[
          { field: 'id' },
          { name: 'photo', field: 'photoSmallUrl', component: 'img' },
          { name: 'First Name', field: 'displayFirstName' },
          { name: 'Last Name', field: 'displayLastName' },
          { name: 'Class', field: member => member.userClass ? member.userClass.name : 'None', component: 'chip' },
          { name: 'Type', field: member => member.membershipType ? member.membershipType.name : 'None', component: 'chip' },
          { name: 'Tier', field: member => member.membershipTier ? member.membershipTier.name : '', component: 'chip' },
          { name: 'Owner', field: 'isMembershipOwner', component: 'checkmark' },
          { name: 'actions', align: 'right' }
        ]"
        :show-add="false"
        :show-edit="false"
        @edit="user => editUser(user)"
      >
        <template
          #row-actions="props"
        >
          <div class="row q-gutter-sm justify-end">
            <q-btn
              label="Set Owner"
              outline
              :loading="savingOwnerId === props.row.id"
              :disable="props.row.isMembershipOwner || !props.row.canBeMembershipOwner"
              @click.stop="setOwner(props.row)"
            />
            <q-tooltip v-if="!props.row.isMembershipOwner && !props.row.canBeMembershipOwner">
              Must have email address and default billing information to be the plan owner
            </q-tooltip>
            <q-tooltip v-else>
              The owner is who pays for the membership when it renews
            </q-tooltip>
          </div>
        </template>
      </model-table>
    </div>
    <div class="q-py-lg">
      <model-table
        :collection="membership.membershipIntervals"
        :columns="[
          { field: 'id' },
          { field: 'state', component: OrderStateCell },
          { field: 'startDate' },
          { field: 'endDate' },
          { name: 'Current', field: 'current', component: 'checkmark' },
          { field: 'amount', component: 'money' },
        ]"
        :cell-class="props => classesForRow(props.row)"
        :show-add="false"
        :show-edit="false"
      />
    </div>
    <q-dialog
      v-model="showRenewalDateDialog"
    >
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">
            Day of Month to Renew
          </div>
        </q-card-section>

        <q-card-section class="q-pt-none">
          <q-select
            v-model.number="billingDayOfMonth"
            dense
            bottom-slots
            :options="$_.range(1, 28)"
          />
        </q-card-section>

        <q-card-actions
          align="right"
          class="text-primary"
        >
          <q-btn
            v-close-popup
            :label="$t('actions.cancel')"
          />
          <q-btn
            v-close-popup
            color="primary"
            label="Next"
            @click="createOrderForChangeRenewalDate"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
    <member-form
      v-show="showMemberForm"
      ref="memberForm"
      :embedded-in-model-table="false"
      simplify

      @apply-changes="memberFormApplyChanges()"
      @add-model="memberFormApplyChanges()"
    />
    <admin-modify-membership
      v-if="$admin"
      ref="adminModifyMembership"
      :membership="membership"
    />
  </model-form>
</template>

<script>

import { date } from 'quasar'
import Membership from 'src/models/membership/Membership'
import { IsoDateTime } from 'src/models/model'
import { USER_TYPES } from 'src/models/user/User'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'
import ModelTable from 'src/modules/admin/components/ModelTable'
import AdminModifyMembership from 'src/modules/admin/membership/AdminModifyMembership'
import OrderStateCell from 'src/modules/admin/order/components/OrderStateCell'
import MemberForm from 'src/modules/register/membership/MemberForm'

export default {
  name: 'MembershipForm',
  components: {
    AdminModifyMembership,
    MemberForm,
    ModelForm,
    ModelTable
  },
  props: {
    teamsForm: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ['modify-membership', 'fix-pending-membership', 'select-order'],
  data: function () {
    return {
      tab: 'memberships',
      assignCardLoading: false,
      savingOwnerId: null,
      savingCanceledAt: false,
      showRenewalDateDialog: false,
      billingDayOfMonth: 1,
      showMemberForm: true,
      editingUser: null
    }
  },
  models: {
    membership: {
      members: {
        membershipType: {
          membershipUserTypes: {
            userClass: true,
            monthlyRate: true,
            additionalMonthlyRate: true
          }
        },
        membershipTier: true,
        userClass: true
      },
      membershipIntervals: {
        amount: true
      },
      owner: true
    }
  },
  created () {
    this.OrderStateCell = OrderStateCell
    this.date = date
  },
  methods: {
    show (model, initialData) {
      this.$refs.modelForm.show(model, initialData)
    },
    classesForRow (membershipInterval) {
      let classes = ''

      if (membershipInterval.state === 'voided') {
        classes += 'bg-red-1 text-strike'
      } else if (membershipInterval.state === 'draft') {
        classes += 'bg-grey-3 text-grey-6'
      } else if (!membershipInterval.current) {
        classes += 'bg-grey-4'
      }

      return classes
    },
    async setRenewal (renew) {
      this.savingCanceledAt = true
      if (renew) {
        this.membership.canceledAt = null
      } else {
        this.membership.canceledAt = new IsoDateTime()
      }

      await this.membership.save()
      this.savingCanceledAt = false
    },
    async setOwner (user) {
      this.savingOwnerId = user.id
      const membership = new Membership({
        id: this.membership.id,
        owner: user
      }, {
        fields: ['id', 'owner']
      })
      await membership.save()
      this.savingOwnerId = null
    },
    modifyMembership () {
      if (this.membership.isDirty) {
        this.$q.dialog({ title: 'Save membership changes first' })
      } else if (this.$admin) {
        this.$refs.adminModifyMembership.show()
      } else {
        this.$refs.modelForm.close()
        this.$emit('modify-membership')
      }
    },
    fixPendingMembership () {
      if (this.membership.isDirty) {
        this.$q.dialog({ title: 'Save membership changes first' })
      } else {
        this.$refs.modelForm.close()
        this.$emit('fix-pending-membership')
      }
    },
    async createOrderForChangeRenewalDate () {
      const { order, errors } = await this.membership.createChangeBillingDayOrder(this.billingDayOfMonth)
      if (errors && errors.length) {
        this.$mh.showErrors(errors)
      } else {
        this.$emit('select-order', order)
      }
    },
    memberFormApplyChanges () {
      this.editingUser.save()
      this.showMemberForm = false
    },
    editUser (user) {
      if (user.type === USER_TYPES.GUEST) {
        this.editingUser = user
        this.$refs.memberForm.show(user)
        this.showMemberForm = true
      } else {
        this.$editModel(user)
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  .poster-img {
    width: 60px;
    height: 80px;
  }
  .banner-img {
    width: 100%;
  }
</style>
